import { HttpError } from '@/lib/httpError'

export interface K8sResource {
  apiVersion: string
  kind: string
  metadata: any
  spec?: any
  status?: any
  type?: string // For resources like Secret that have a type field
}

export interface K8sResourceListResponse {
  apiVersion: string
  kind: string
  items: K8sResource[]
  metadata: {
    resourceVersion?: string
    continue?: string
  }
}

class K8sResourceService {
  private baseUrl = '/api/v2/k8s/resources'

  /**
   * Clean manifest by removing system-managed fields
   * Removes metadata.managedFields and status
   */
  private cleanManifest(manifest: any): any {
    const cleaned = { ...manifest }

    // Remove managedFields from metadata
    if (cleaned.metadata) {
      const { managedFields, ...restMetadata } = cleaned.metadata
      cleaned.metadata = restMetadata
    }

    // Remove status
    if (cleaned.status) {
      delete cleaned.status
    }

    return cleaned
  }

  async getResources(
    group: string = '',
    version: string,
    kind: string,
    namespace?: string,
    continueToken?: string
  ): Promise<K8sResourceListResponse> {
    const params = new URLSearchParams()
    params.append('group', group)
    params.append('version', version)
    params.append('kind', kind)
    if (namespace) {
      params.append('namespace', namespace)
    }
    if (continueToken) {
      params.append('continue', continueToken)
    }

    const response = await fetch(`${this.baseUrl}?${params.toString()}`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
      },
      credentials: 'include',
    })

    if (!response.ok) {
      const errorText = await response.text()
      throw new HttpError(errorText || 'Failed to fetch K8s resources', response.status)
    }

    return response.json()
  }

  async getNodes(continueToken?: string): Promise<K8sResourceListResponse> {
    return this.getResources('', 'v1', 'node', undefined, continueToken)
  }

  async getFlows(namespace?: string, continueToken?: string): Promise<K8sResourceListResponse> {
    return this.getResources('synstream.rtsinv.com', 'v1', 'Flow', namespace, continueToken)
  }

  extractNodeIPs(nodeList: K8sResourceListResponse): string[] {
    const ips: string[] = []

    if (!nodeList.items || !Array.isArray(nodeList.items)) {
      return ips
    }

    for (const node of nodeList.items) {
      if (node.status?.addresses && Array.isArray(node.status.addresses)) {
        for (const addr of node.status.addresses) {
          if (addr.type === 'InternalIP' || addr.type === 'ExternalIP') {
            ips.push(addr.address)
          }
        }
      }
    }

    return ips
  }

  async deleteResource(
    group: string,
    version: string,
    kind: string,
    namespace: string,
    name: string
  ): Promise<void> {
    const params = new URLSearchParams()
    params.append('group', group)
    params.append('version', version)
    params.append('kind', kind)
    params.append('namespace', namespace)
    params.append('name', name)

    const response = await fetch(`${this.baseUrl}?${params.toString()}`, {
      method: 'DELETE',
      headers: {
        'Content-Type': 'application/json',
      },
      credentials: 'include',
    })

    if (!response.ok) {
      const errorText = await response.text()
      throw new HttpError(errorText || 'Failed to delete resource', response.status)
    }
  }

  async requestDeleteResource(
    group: string,
    version: string,
    kind: string,
    namespace: string,
    name: string
  ): Promise<void> {
    const params = new URLSearchParams()
    params.append('group', group)
    params.append('version', version)
    params.append('kind', kind)
    params.append('namespace', namespace)
    params.append('name', name)

    const response = await fetch(`${this.baseUrl}/delete/request?${params.toString()}`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      credentials: 'include',
    })

    if (!response.ok) {
      const errorText = await response.text()
      throw new HttpError(errorText || 'Failed to request resource deletion', response.status)
    }
  }

  async deleteFlow(namespace: string, name: string): Promise<void> {
    return this.deleteResource('synstream.rtsinv.com', 'v1', 'Flow', namespace, name)
  }

  async requestDeleteFlow(namespace: string, name: string): Promise<void> {
    return this.requestDeleteResource('synstream.rtsinv.com', 'v1', 'Flow', namespace, name)
  }

  async restartDeployment(namespace: string, name: string): Promise<void> {
    return this.deleteResource('apps', 'v1', 'Deployment', namespace, name)
  }

  async requestRestartDeployment(namespace: string, name: string): Promise<void> {
    return this.requestDeleteResource('apps', 'v1', 'Deployment', namespace, name)
  }

  async createResource(manifest: any): Promise<void> {
    const response = await fetch(this.baseUrl, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      credentials: 'include',
      body: JSON.stringify({ manifest }),
    })

    if (!response.ok) {
      const errorText = await response.text()
      throw new HttpError(errorText || 'Failed to create resource', response.status)
    }
  }

  async requestCreateResource(manifest: any): Promise<void> {
    const response = await fetch(`${this.baseUrl}/request`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      credentials: 'include',
      body: JSON.stringify({ manifest }),
    })

    if (!response.ok) {
      const errorText = await response.text()
      throw new HttpError(errorText || 'Failed to request resource creation', response.status)
    }
  }

  async updateResource(manifest: any): Promise<void> {
    // Clean manifest before sending
    const cleanedManifest = this.cleanManifest(manifest)

    const response = await fetch(this.baseUrl, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
      },
      credentials: 'include',
      body: JSON.stringify({ manifest: cleanedManifest }),
    })

    if (!response.ok) {
      const errorText = await response.text()
      throw new HttpError(errorText || 'Failed to update resource', response.status)
    }
  }

  async requestUpdateResource(manifest: any): Promise<void> {
    // Clean manifest before sending
    const cleanedManifest = this.cleanManifest(manifest)

    const response = await fetch(`${this.baseUrl}/update/request`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      credentials: 'include',
      body: JSON.stringify({ manifest: cleanedManifest }),
    })

    if (!response.ok) {
      const errorText = await response.text()
      throw new HttpError(errorText || 'Failed to request resource update', response.status)
    }
  }

  async getResource(
    name: string,
    group: string,
    version: string,
    kind: string,
    namespace: string
  ): Promise<K8sResource> {
    const params = new URLSearchParams()
    params.append('group', group)
    params.append('version', version)
    params.append('kind', kind)
    params.append('namespace', namespace)

    const response = await fetch(`${this.baseUrl}/${name}?${params.toString()}`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
      },
      credentials: 'include',
    })

    if (!response.ok) {
      const errorText = await response.text()
      throw new HttpError(errorText || 'Failed to fetch resource', response.status)
    }

    return response.json()
  }

  async getFlow(namespace: string, name: string): Promise<K8sResource> {
    return this.getResource(name, 'synstream.rtsinv.com', 'v1', 'Flow', namespace)
  }

  async getSecrets(namespace: string, continueToken?: string): Promise<K8sResourceListResponse> {
    return this.getResources('', 'v1', 'Secret', namespace, continueToken)
  }

  async getLicenses(continueToken?: string): Promise<K8sResourceListResponse> {
    const response = await this.getResources('synstream.rtsinv.com', 'v1', 'License', undefined, continueToken)
    // The License CRD is cluster-scoped; the operator-managed "synstream-license"
    // is the single canonical object. A stray manually-created License (e.g. one
    // named "license") would otherwise render as a duplicate card. Prefer the
    // canonical object when present, but fall back to the raw list if it is
    // missing so a legacy-named license is still shown. (SYN-125)
    const canonical = response.items.filter((l) => l.metadata?.name === 'synstream-license')
    return canonical.length > 0 ? { ...response, items: canonical } : response
  }

  async getPVCs(namespace: string, continueToken?: string): Promise<K8sResourceListResponse> {
    return this.getResources('', 'v1', 'PersistentVolumeClaim', namespace, continueToken)
  }

  async getIngressClasses(continueToken?: string): Promise<K8sResourceListResponse> {
    return this.getResources('networking.k8s.io', 'v1', 'IngressClass', undefined, continueToken)
  }

  // Helper methods to extract data from K8s resources
  extractSecretNames(secretList: K8sResourceListResponse): string[] {
    if (!secretList.items || !Array.isArray(secretList.items)) {
      return []
    }
    return secretList.items.map(secret => secret.metadata?.name).filter((name): name is string => !!name)
  }

  extractTLSSecretNames(secretList: K8sResourceListResponse): string[] {
    if (!secretList.items || !Array.isArray(secretList.items)) {
      return []
    }
    return secretList.items
      .filter(secret => secret.type === 'kubernetes.io/tls')
      .map(secret => secret.metadata?.name)
      .filter((name): name is string => !!name)
  }

  extractPVCNames(pvcList: K8sResourceListResponse): string[] {
    if (!pvcList.items || !Array.isArray(pvcList.items)) {
      return []
    }
    return pvcList.items.map(pvc => pvc.metadata?.name).filter((name): name is string => !!name)
  }

  extractIngressClassNames(ingressClassList: K8sResourceListResponse): string[] {
    if (!ingressClassList.items || !Array.isArray(ingressClassList.items)) {
      return []
    }
    return ingressClassList.items.map(ic => ic.metadata?.name).filter((name): name is string => !!name)
  }

  getPVCSize(pvcList: K8sResourceListResponse, pvcName: string): string | null {
    if (!pvcList.items || !Array.isArray(pvcList.items)) {
      return null
    }
    const pvc = pvcList.items.find(p => p.metadata?.name === pvcName)
    return pvc?.spec?.resources?.requests?.storage || null
  }

  getConfigMaps(namespace: string, continueToken?: string): Promise<K8sResourceListResponse> {
    return this.getResources('', 'v1', 'ConfigMap', namespace, continueToken)
  }

  extractConfigMapNames(configMapList: K8sResourceListResponse): string[] {
    if (!configMapList.items || !Array.isArray(configMapList.items)) {
      return []
    }
    return configMapList.items.map(cm => cm.metadata?.name).filter((name): name is string => !!name)
  }

  getStorageClasses(continueToken?: string): Promise<K8sResourceListResponse> {
    return this.getResources('storage.k8s.io', 'v1', 'StorageClass', undefined, continueToken)
  }

  extractStorageClassNames(storageClassList: K8sResourceListResponse): string[] {
    if (!storageClassList.items || !Array.isArray(storageClassList.items)) {
      return []
    }
    return storageClassList.items.map(sc => sc.metadata?.name).filter((name): name is string => !!name)
  }
}

export const k8sResourceService = new K8sResourceService()
